import { useState } from "react";
import { useClientNotes, useAddClientNote, useDeleteClientNote } from "../lib/queries";
import { LoadingState, ErrorState } from "./Loading";

interface ClientNoteRow {
  id: string;
  note: string;
  created_at: string;
}

/**
 * The coach's private chart on one client: a running, dated record of observations.
 *
 * Notes used to hang off a plan, which is the wrong shape -- "she guards the left ankle on landings" is
 * true of the skater, not of whichever programme happened to be published the week it was noticed, and
 * a plan-scoped note vanished from view the moment the next plan replaced it. Read like a patient
 * chart: newest at the top, every entry dated, nothing overwritten.
 *
 * Coach-only. GET/POST/DELETE /clients/{id}/notes are coach-and-owner-checked server-side, so this
 * component being rendered on a coach page is presentation, not the access boundary.
 */
export function ClientNotes({ clientId }: { clientId: string }) {
  const notesQuery = useClientNotes<ClientNoteRow[]>(clientId);
  const addNote = useAddClientNote(clientId);
  const deleteNote = useDeleteClientNote(clientId);
  const [draft, setDraft] = useState("");

  function submit() {
    const text = draft.trim();
    if (!text) return;
    addNote.mutate(text, { onSuccess: () => setDraft("") });
  }

  return (
    <>
      <h2>Private notes</h2>
      <p className="form-hint">
        Only you can see these. The athlete, their guardian, and anyone else on the account never do.
      </p>

      <div className="note-composer">
        <textarea
          aria-label="New note"
          rows={3}
          value={draft}
          maxLength={2000}
          onChange={(event) => setDraft(event.target.value)}
          placeholder="What did you notice today?"
        />
        <button type="button" disabled={addNote.isPending || !draft.trim()} onClick={submit}>
          {addNote.isPending ? "Saving…" : "Add note"}
        </button>
      </div>
      {addNote.isError && <ErrorState error={addNote.error} />}
      {deleteNote.isError && <ErrorState error={deleteNote.error} />}

      {notesQuery.isPending && <LoadingState label="Loading notes…" />}
      {notesQuery.isError && <ErrorState error={notesQuery.error} onRetry={() => notesQuery.refetch()} />}
      {notesQuery.isSuccess &&
        (notesQuery.data.length === 0 ? (
          <p className="empty-state">No notes on this client yet.</p>
        ) : (
          <ul className="note-list">
            {notesQuery.data.map((note) => (
              <li key={note.id} className="note-entry">
                <div className="note-entry-head">
                  <span className="note-date">{formatNoteDate(note.created_at)}</span>
                  {/* Deliberately unguarded by a confirm: a note is a few seconds of typing, and a
                      dialog on every tidy-up is friction out of proportion to what is lost. */}
                  <button
                    type="button"
                    className="icon-button"
                    aria-label={`Delete note from ${formatNoteDate(note.created_at)}`}
                    disabled={deleteNote.isPending}
                    onClick={() => deleteNote.mutate(note.id)}
                  >
                    ✕
                  </button>
                </div>
                <p className="note-body">{note.note}</p>
              </li>
            ))}
          </ul>
        ))}
    </>
  );
}

/** Date and time -- two notes on the same day are common, and ordering them by eye needs the clock. */
function formatNoteDate(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}
